import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import { useMemo, useState } from 'react';
import { useLazyGetBillableHoursByMonthReportQuery } from 'store/api/reportingTrends';
import { BillableHoursByMonthRow, BillableHoursTotals } from 'types/reporting/trends';
import { t } from 'utils/getTranslations';
import { ChartPanel } from '../charts/ChartPanel';
import { fullMonthLabel, monthLabel, monthNames, spansYears } from '../charts/chartFormat';
import { ChartReportView } from '../charts/ChartReportView';
import { PivotSpec, sumByKey } from '../charts/pivotData';
import { PivotTable } from '../charts/PivotTable';
import { ReportBarChart } from '../charts/ReportBarChart';
import { currentYearSearchParams, yearFilter } from '../charts/trendFilters';
import { formatReportNumber, pivotToneRule, ReportTone } from '../reportCells';
import { ReportEntry } from '../reportCatalog';
import { ReportLayout } from '../ReportLayout';
import { quickFiltersFrom } from '../reportFilters';

const TONE: ReportTone = 'yellow';

type Row = BillableHoursByMonthRow;

/** Years down the side, the months of the year across: one year's row reads January to December. */
const PIVOT_SPEC: PivotSpec<Row> = {
    rowKey: (row) => [`${row.year}`],
    columnKey: (row) => row.month.slice(5, 7),
    value: (row) => row.billableHours,
};

const MONTH_NAMES = monthNames();

function monthOfYearLabel(column: string): string {
    return MONTH_NAMES[Number(column) - 1].slice(0, 3);
}

function buildFilterConfig(today: Date): FilterConfig {
    const filters = [yearFilter(today)];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        quickFilters: quickFiltersFrom(filters, { year: 'reporting.filter.year' }),
        searchBarSelectors: [{ selector: 'monthName', comparator: '=lk=' }],
        filters,
    };
}

function BillableHoursByMonthCharts({ rows, totals }: { rows: Row[]; totals: BillableHoursTotals }) {
    const measure = t('reporting.column.billable-hours');
    const withYear = spansYears(rows.map((row) => row.month));
    const bars = sumByKey(
        rows,
        (row) => row.month,
        (row) => row.billableHours
    ).map(({ key, value }) => ({
        key,
        label: monthLabel(key, withYear),
        tooltipLabel: fullMonthLabel(key),
        value,
    }));
    const total = t('reporting.chart.total-hours', { value: formatReportNumber(totals.billableHours) });
    return (
        <>
            <ChartPanel title={t('reporting.billable-hours-by-month.chart')} aside={total}>
                <ReportBarChart bars={bars} measure={measure} />
            </ChartPanel>
            <ChartPanel title={t('reporting.billable-hours-by-month.table')} flush>
                <PivotTable
                    rows={rows}
                    spec={PIVOT_SPEC}
                    rowHeaders={[t('reporting.column.year')]}
                    columnLabel={monthOfYearLabel}
                    label={t('reporting.billable-hours-by-month.table')}
                    showRowTotals
                    tone={TONE}
                />
            </ChartPanel>
        </>
    );
}

function renderCharts(rows: Row[], totals: BillableHoursTotals) {
    return <BillableHoursByMonthCharts rows={rows} totals={totals} />;
}

export function BillableHoursByMonthReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetBillableHoursByMonthReportQuery();
    const [today] = useState(() => new Date());
    const [initialSearchParams] = useState(() => currentYearSearchParams(today));
    const filterConfig = useMemo(() => buildFilterConfig(today), [today]);
    return (
        <ReportLayout
            report={report}
            toneLegend={[{ label: t('reporting.column.billable-hours'), rule: pivotToneRule(TONE) }]}
        >
            <ChartReportView<Row, BillableHoursTotals>
                analyticsSurface="report-billable-hours-by-month"
                data={data}
                isFetching={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                initialSearchParams={initialSearchParams}
            >
                {renderCharts}
            </ChartReportView>
        </ReportLayout>
    );
}
